import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '../mensagemBanco';

type Chave = 'pede_5_dias' | 'pede_pastoreio' | 'pede_pastoral' | 'pede_experiencia';

const CHECAGENS: { chave: Chave; rotulo: string }[] = [
  { chave: 'pede_5_dias', rotulo: 'Ficar os 5 dias' },
  { chave: 'pede_pastoreio', rotulo: 'Fazer o curso Pastoreio' },
  { chave: 'pede_pastoral', rotulo: 'Participar de pastoral / vida na paróquia' },
  { chave: 'pede_experiencia', rotulo: 'Já ter servido antes (experiência)' },
];

const SEM_CRITERIOS: Record<Chave, boolean> = {
  pede_5_dias: false,
  pede_pastoreio: false,
  pede_pastoral: false,
  pede_experiencia: false,
};

const LIMITE_TEXTO = 800;

interface Props {
  equipeId: number;
  nome: string;
  aoToast: (msg: string) => void;
  aoMudou: () => void;
}

interface Palavra {
  palavra: string;
  origem: string;
}

function Interruptor({
  ligado,
  aoMudar,
  desativado,
}: {
  ligado: boolean;
  aoMudar: () => void;
  desativado: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      disabled={desativado}
      onClick={aoMudar}
      className={`relative h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        ligado ? 'bg-primary-500' : 'bg-secondary-300'
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
          ligado ? 'left-[22px]' : 'left-0.5'
        }`}
      />
    </button>
  );
}

export default function BlocoSobreEquipe({ equipeId, nome, aoToast, aoMudou }: Props) {
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState(false);
  const [funcoes, setFuncoes] = useState('');
  const [texto, setTexto] = useState('');
  const [criterios, setCriterios] = useState<Record<Chave, boolean>>(SEM_CRITERIOS);
  const [palavras, setPalavras] = useState<Palavra[]>([]);
  const [padrao, setPadrao] = useState<string[]>([]);
  const [salvandoTexto, setSalvandoTexto] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [salvandoChave, setSalvandoChave] = useState<Chave | null>(null);
  const [nova, setNova] = useState('');
  const [salvandoPalavra, setSalvandoPalavra] = useState(false);
  const [copiandoPadrao, setCopiandoPadrao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    if (!salvo) return;
    const t = setTimeout(() => setSalvo(false), 2500);
    return () => clearTimeout(t);
  }, [salvo]);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const [resEquipe, resPalavras, resPadrao] = await Promise.all([
        supabase
          .from('equipes_evento')
          .select('id, funcoes, pede_5_dias, pede_pastoreio, pede_pastoral, pede_experiencia')
          .eq('id', equipeId)
          .maybeSingle(),
        supabase
          .from('palavras_equipe_evento')
          .select('palavra, origem')
          .eq('equipe_id', equipeId)
          .order('palavra'),
        supabase.from('palavras_equipe').select('palavra').eq('equipe', nome).order('palavra'),
      ]);
      if (resEquipe.error) throw resEquipe.error;
      if (resPalavras.error) throw resPalavras.error;
      if (resPadrao.error) throw resPadrao.error;
      if (!montado.current) return;

      const linha = (resEquipe.data ?? null) as {
        funcoes: string | null;
        pede_5_dias: boolean | null;
        pede_pastoreio: boolean | null;
        pede_pastoral: boolean | null;
        pede_experiencia: boolean | null;
      } | null;

      const textoAtual = linha?.funcoes ?? '';
      setFuncoes(textoAtual);
      setTexto(textoAtual);
      setCriterios({
        pede_5_dias: !!linha?.pede_5_dias,
        pede_pastoreio: !!linha?.pede_pastoreio,
        pede_pastoral: !!linha?.pede_pastoral,
        pede_experiencia: !!linha?.pede_experiencia,
      });
      setPalavras((resPalavras.data as Palavra[]) ?? []);
      setPadrao(((resPadrao.data as { palavra: string }[]) ?? []).map((p) => p.palavra));
      setAberto(!textoAtual.trim());
    } catch (e) {
      if (!montado.current) return;
      setErro(mensagemBanco(e));
    } finally {
      if (montado.current) setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipeId, nome]);

  async function salvarTexto() {
    setSalvandoTexto(true);
    setErro(null);
    try {
      const { error } = await supabase
        .from('equipes_evento')
        .update({ funcoes: texto.trim() })
        .eq('id', equipeId);
      if (error) throw error;
      setFuncoes(texto.trim());
      setSalvo(true);
      aoMudou();
    } catch (e) {
      aoToast(mensagemBanco(e));
    } finally {
      setSalvandoTexto(false);
    }
  }

  async function alternar(chave: Chave) {
    const novo = !criterios[chave];
    setCriterios((prev) => ({ ...prev, [chave]: novo }));
    setSalvandoChave(chave);
    try {
      const { error } = await supabase
        .from('equipes_evento')
        .update({ [chave]: novo })
        .eq('id', equipeId);
      if (error) throw error;
      aoMudou();
    } catch (e) {
      setCriterios((prev) => ({ ...prev, [chave]: !novo }));
      aoToast(mensagemBanco(e));
    } finally {
      setSalvandoChave(null);
    }
  }

  async function recarregarPalavras() {
    const { data, error } = await supabase
      .from('palavras_equipe_evento')
      .select('palavra, origem')
      .eq('equipe_id', equipeId)
      .order('palavra');
    if (error) throw error;
    setPalavras((data as Palavra[]) ?? []);
  }

  async function acrescentar() {
    const limpa = nova.trim().toLowerCase();
    if (limpa.length < 3) {
      aoToast('A palavra precisa ter pelo menos 3 letras.');
      return;
    }
    if (palavras.some((p) => p.palavra === limpa)) {
      aoToast('Essa palavra já está na lista.');
      return;
    }
    setSalvandoPalavra(true);
    try {
      const { error } = await supabase
        .from('palavras_equipe_evento')
        .insert({ equipe_id: equipeId, palavra: limpa, origem: 'direcao' });
      if (error) throw error;
      setNova('');
      await recarregarPalavras();
      aoMudou();
    } catch (e) {
      aoToast(mensagemBanco(e));
    } finally {
      setSalvandoPalavra(false);
    }
  }

  async function remover(palavra: string) {
    try {
      const { error } = await supabase
        .from('palavras_equipe_evento')
        .delete()
        .eq('equipe_id', equipeId)
        .eq('palavra', palavra);
      if (error) throw error;
      await recarregarPalavras();
      aoMudou();
    } catch (e) {
      aoToast(mensagemBanco(e));
    }
  }

  async function usarPadraoComoBase() {
    if (padrao.length === 0) return;
    setCopiandoPadrao(true);
    try {
      const { error } = await supabase.from('palavras_equipe_evento').insert(
        padrao.map((palavra) => ({ equipe_id: equipeId, palavra, origem: 'padrao' })),
      );
      if (error) throw error;
      await recarregarPalavras();
      aoMudou();
    } catch (e) {
      aoToast(mensagemBanco(e));
    } finally {
      setCopiandoPadrao(false);
    }
  }

  const temProprias = palavras.length > 0;
  const ligados = CHECAGENS.filter((c) => criterios[c.chave]).length;
  const primeiraLinha = (funcoes || '').split('\n')[0].trim();
  const resumo = [
    ligados === 1 ? '1 critério' : `${ligados} critérios`,
    temProprias ? `${palavras.length} palavras` : 'palavras padrão',
  ].join(' · ');

  if (carregando) {
    return <div className="mb-4 h-12 animate-pulse rounded-2xl bg-secondary-100" />;
  }

  return (
    <div className="mb-4 rounded-2xl border border-secondary-200 bg-white">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground-950">
            Sobre esta equipe
          </span>
          {!aberto && (
            <span className="mt-0.5 block truncate text-xs text-secondary-500">
              {primeiraLinha ? `${primeiraLinha} · ${resumo}` : resumo}
            </span>
          )}
        </span>
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-secondary-500">
          <i className={aberto ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </span>
      </button>

      {aberto && (
        <div className="border-t border-secondary-200 px-4 py-4">
          {erro && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {erro}
            </div>
          )}

          <label
            htmlFor={`funcoes-equipe-${equipeId}`}
            className="block text-sm font-semibold text-foreground-950"
          >
            Funções desta equipe
          </label>
          <textarea
            id={`funcoes-equipe-${equipeId}`}
            value={texto}
            maxLength={LIMITE_TEXTO}
            rows={4}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Ex.: cuida da limpeza do lugar: lava os banheiros, recolhe o lixo, varre o barracão."
            className="mt-2 w-full box-border resize-y rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-400"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-secondary-500">
              {texto.length}/{LIMITE_TEXTO}
            </span>
            <div className="flex items-center gap-2">
              {salvo && (
                <span className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-green-700">
                  <i className="ri-check-line" />
                  Salvo ✓
                </span>
              )}
              <button
                type="button"
                onClick={salvarTexto}
                disabled={salvandoTexto || texto.trim() === funcoes.trim()}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {salvandoTexto ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>

          <div className="mt-5 border-t border-secondary-200 pt-4">
            <p className="text-sm font-semibold text-foreground-950">
              O que a pessoa precisa ter
            </p>
            <div className="mt-2 space-y-1">
              {CHECAGENS.map((c) => (
                <div
                  key={c.chave}
                  className="flex items-center justify-between gap-3 rounded-md px-1 py-1.5"
                >
                  <span className="min-w-0 text-sm text-foreground-800">{c.rotulo}</span>
                  <Interruptor
                    ligado={criterios[c.chave]}
                    desativado={salvandoChave === c.chave}
                    aoMudar={() => alternar(c.chave)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 border-t border-secondary-200 pt-4">
            <p className="text-sm font-semibold text-foreground-950">Palavras que combinam</p>

            {temProprias ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {palavras.map((p) => (
                  <span
                    key={p.palavra}
                    className="flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 py-1 pl-2.5 pr-1 text-xs font-medium text-secondary-900"
                  >
                    {p.palavra}
                    <button
                      type="button"
                      onClick={() => remover(p.palavra)}
                      title="Tirar palavra"
                      className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full text-secondary-600 transition-colors hover:bg-secondary-300"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </span>
                ))}
              </div>
            ) : padrao.length > 0 ? (
              <>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {padrao.map((p) => (
                    <span
                      key={p}
                      className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-500"
                    >
                      {p}
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-secondary-500">
                  Palavras padrão. Acrescente as suas para personalizar.
                </p>
                <button
                  type="button"
                  onClick={usarPadraoComoBase}
                  disabled={copiandoPadrao}
                  className="mt-2 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {copiandoPadrao ? 'Copiando…' : 'Usar estas como base'}
                </button>
              </>
            ) : (
              <p className="mt-2 text-xs text-secondary-500">
                Esta equipe ainda não tem palavras. Acrescente as suas abaixo.
              </p>
            )}

            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={nova}
                onChange={(e) => setNova(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') acrescentar();
                }}
                placeholder="Acrescentar palavra"
                className="min-w-0 w-full box-border rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-400"
              />
              <button
                type="button"
                onClick={acrescentar}
                disabled={salvandoPalavra || nova.trim().length < 3}
                className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {salvandoPalavra ? 'Salvando…' : 'Acrescentar'}
              </button>
            </div>

            <p className="mt-2 text-xs text-secondary-500">
              O sistema procura estas palavras na profissão e nos talentos de cada pessoa para
              sugerir quem combina.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}